import React from 'react';
import { Trans } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import Tandemz from '@app/illu/Branding/Tandemz.svg';

const useStyles = createUseStyles((theme: Theme) => ({
  h1: {
    ...theme.fonts.h3,
    marginBottom: theme.marginBase * 2,
  },
  error: {
    ...theme.fonts.caption,
    color: theme.colors.red,
  },
  link: {
    ...theme.fonts.label,
    color: theme.colors.blueAccessible,
  },
  recruitNowBlock: {
    ...theme.fonts.caption,
    marginTop: theme.marginBase * 5,
    '& > p': {
      marginBottom: theme.marginBase,
    },
  },
  illustration: {
    width: '100%',
    maxWidth: 590,
  },
  forgottenPasswordLink: {
    ...theme.fonts.caption,
    color: theme.colors.blueAccessible,
  },
  container: {
    display: 'flex',
    height: '100%',
    width: '100%',
    [theme.media.tablet]: {
      flexDirection: 'column',
    },
  },
  panel: {
    background: theme.colors.redMuted,
    flex: 1,
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    flexDirection: 'column',
    padding: theme.marginBase * 8,
    [theme.media.tablet]: {
      flexDirection: 'column',
      padding: 0,
      flex: 'unset',
    },
  },
  formPane: {
    width: '100%',
    maxWidth: theme.maxContentWidth.reducedFormContent + theme.marginBase * 20, // + padding
    position: 'relative',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    [theme.media.tablet]: {
      borderTopLeftRadius: theme.borderRadius.large,
      borderTopRightRadius: theme.borderRadius.large,
      flex: 1,
      maxWidth: 'none',
      marginTop: -theme.marginBase * 2,
    },
    [theme.media.mobile]: {
      alignItems: 'flex-start',
    },
  },
  centerBox: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: `${theme.marginBase * 7}px ${theme.marginBase * 10}px`,
    [theme.media.tablet]: {
      padding: `${theme.marginBase * 4}px ${theme.marginBase * 2}px`,
      maxWidth: theme.maxContentWidth.reduced,
    },
  },
  panelContainer: {
    width: '100%',
    flex: 1,
    display: 'flex',
    justifyContent: 'flex-start',
    marginTop: -50,
    alignItems: 'center',
    [theme.media.tablet]: {
      display: 'none',
    },
  },
  headerContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
    [theme.media.tablet]: {
      padding: [
        theme.marginBase * 3,
        theme.marginBase * 3,
        theme.marginBase * 6,
        theme.marginBase * 3,
      ],
    },
  },
  titleDescription: {
    ...theme.fonts.h1,
    color: theme.colors.inkPrimary,
  },
  titleDescriptionStrong: {
    color: theme.colors.red,
  },
}));

interface Props {
  children?: React.ReactNode;
}

export const AuthPane = ({ children }: Props) => {
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <div className={classes.panel}>
        <div className={classes.headerContainer}>
          <img src={Tandemz} alt="tandemz" />
        </div>
        <div className={classes.panelContainer}>
          <h1 className={classes.titleDescription}>
            <Trans
              i18nKey="auth.login.description"
              components={{
                strong: <span className={classes.titleDescriptionStrong} />,
                br: <br />,
              }}
            />
          </h1>
        </div>
      </div>
      <div className={classes.formPane}>
        <div className={classes.centerBox}>{children}</div>
      </div>
    </div>
  );
};
